import { create } from 'zustand';
import {
  getQuestions,
  getAssessments,
  getAvailableQuestions,
  getCarveAssessmentSources,
  createQuestion as apiCreateQuestion,
  updateQuestion as apiUpdateQuestion,
  deleteQuestion as apiDeleteQuestion,
  Question,
  QuestionPayload,
  Assessment,
  AvailableQuestion,
  CarveAssessmentSource,
} from '@/services/PagesServices/AssessmentManagementService';

interface AssessmentManagementState {
  questions: Question[] | null;
  assessments: Assessment[] | null;
  availableQuestions: AvailableQuestion[] | null;
  carveAssessmentSources: CarveAssessmentSource[] | null;
  loading: boolean;
  error: string | null;

  fetchQuestions: () => Promise<void>;
  fetchAssessments: () => Promise<void>;
  fetchAvailableQuestions: () => Promise<void>;
  fetchCarveAssessmentSources: () => Promise<void>;

  // Question actions — async; resolve to the persisted question (or throw)
  addQuestion: (payload: QuestionPayload) => Promise<Question>;
  updateQuestion: (id: number, payload: Partial<QuestionPayload>) => Promise<Question>;
  deleteQuestion: (questionId: number) => Promise<void>;
}

const useAssessmentManagementStore = create<AssessmentManagementState>((set, get) => ({
  questions: null,
  assessments: null,
  availableQuestions: null,
  carveAssessmentSources: null,
  loading: false,
  error: null,

  fetchQuestions: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getQuestions();
      set({
        questions: response.data || [],
        loading: false,
        error: response.success ? null : response.error?.message ?? null,
      });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchAssessments: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAssessments();
      set({ assessments: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchAvailableQuestions: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAvailableQuestions();
      set({ availableQuestions: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchCarveAssessmentSources: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCarveAssessmentSources();
      set({ carveAssessmentSources: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  addQuestion: async (payload: QuestionPayload) => {
    const response = await apiCreateQuestion(payload);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create question');
    }
    const created = response.data;
    const { questions } = get();
    set({ questions: [created, ...(questions ?? [])] });
    return created;
  },

  updateQuestion: async (id: number, payload: Partial<QuestionPayload>) => {
    const response = await apiUpdateQuestion(id, payload);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to update question');
    }
    const updated = response.data;
    const { questions } = get();
    if (questions) {
      set({
        questions: questions.map((q) => (q.id === updated.id ? updated : q)),
      });
    }
    return updated;
  },

  deleteQuestion: async (id: number) => {
    const response = await apiDeleteQuestion(id);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete question');
    }
    const { questions } = get();
    if (questions) {
      set({ questions: questions.filter((q) => q.id !== id) });
    }
  },
}));

export default useAssessmentManagementStore;
